import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  ButtonGroup,
  Card,
  Checkbox,
  Collapsible,
  DataTable,
  FormLayout,
  InlineGrid,
  InlineStack,
  Link,
  List,
  Modal,
  Select,
  SkeletonBodyText,
  Text,
  TextField
} from '@shopify/polaris';
import { Bar, BarChart, CartesianGrid, Legend, ReferenceLine, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { deleteRequest, getRequest, simplePostRequest } from '../../../../shared/components/functions';
import { CardHeading } from '../../../../shared/components/cards/CardHeading';
import { formatCompactCurrency, formatShortDate, toNumber } from './analyticsHelpers';
import {
  BalanceChart,
  COMPONENTS,
  EventChips,
  ITEM_TYPES,
  addDaysIso,
  buildBalanceChartData,
  buildExpectedSeries,
  buildIntercompanySeries,
  collectChartEvents,
  eur,
  summarizeHorizon
} from './cashPlanningShared';

/*
  Cash planning: a simple 30/60-day bank balance projection.

    balance today
      + open customer invoices (due date + the customer's usual delay)
      + sales-order backlog (toggle)
      - open supplier invoices, committed payments, purchase orders (toggle)
      - open straight loans repaid on their end date
      - recurring bank flows learned from bank history (loans "071-", salaries
        "loon", social secretariat, VAT, card, insurance, ...) — weighted
        average of the last 5 months on the days they usually hit, or a
        manual monthly override
      + manual planned items

  "Expected" uses medians; "Cautious" makes customers pay at their 75th
  percentile, drops overdue > 90 days and not-yet-invoiced orders, and pays
  suppliers on the due date. Both leave intercompany documents (Aha
  Innovations, Jadibo, Adriaens NV) out; the dotted "incl. intercompany" line
  adds them back with their expected timing. Everything is computed server-side
  (/api/cash-planning/plan); the layer toggles only re-sum the daily
  per-component flows that come with the response.
*/

const signed = (value) => (
  <Text as="span" tone={toNumber(value) < 0 ? 'critical' : toNumber(value) > 0 ? 'success' : 'subdued'}>
    {toNumber(value) === 0 ? '—' : eur(value)}
  </Text>
);

const RECURRING_COLORS = {
  loan: '#5B6DC9',
  salary: '#2C9F79',
  social: '#D4703B',
  tax: '#B14C6C',
  card: '#8A6FC4',
  insurance: '#3C9DC8',
  bank_fees: '#9AA4AF',
  expenses: '#C9A227'
};

const monthLabel = (key) => {
  const [year, month] = String(key).split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString('en-GB', { month: 'short', year: '2-digit', timeZone: 'UTC' });
};

//----------------------------------------------------------------------------
// small building blocks
//----------------------------------------------------------------------------
function StatCard({ title, value, detail, tone }) {
  return (
    <Card>
      <BlockStack gap="100">
        <Text tone="subdued" as="p">
          {title}
        </Text>
        <Text variant="headingLg" as="p" tone={tone}>
          {value}
        </Text>
        {detail ? (
          <Text tone="subdued" as="p" variant="bodySm">
            {detail}
          </Text>
        ) : null}
      </BlockStack>
    </Card>
  );
}

function itemRow(item) {
  const config = ITEM_TYPES[item.type] || { label: item.type, tone: undefined, linkBase: null };
  return [
    item.coveredBy ? 'in recurring flows' : item.expectedDate ? formatShortDate(item.expectedDate) : item.beyondHorizon ? '> horizon' : 'not expected',
    item.cautiousDate ? formatShortDate(item.cautiousDate) : '—',
    item.intercompany ? (
      <InlineStack gap="100" wrap={false}>
        <Badge tone={config.tone}>{config.label}</Badge>
        <Badge tone="magic">Intercompany</Badge>
      </InlineStack>
    ) : (
      <Badge tone={config.tone}>{config.label}</Badge>
    ),
    config.linkBase ? (
      <Link removeUnderline url={`${config.linkBase}/${item.id}`}>
        {item.number || item.id}
      </Link>
    ) : (
      String(item.number || item.id)
    ),
    item.partnerId ? (
      <Link removeUnderline url={`/companies/${item.partnerId}`}>
        {String(item.partnerName || 'Unknown').substring(0, 32)}
      </Link>
    ) : (
      String(item.partnerName || 'Unknown').substring(0, 32)
    ),
    item.dueDate ? formatShortDate(item.dueDate) : '—',
    signed(item.amount)
  ];
}

const ITEM_TABLE_PROPS = {
  verticalAlign: 'middle',
  increasedTableDensity: true,
  stickyHeader: true,
  columnContentTypes: ['text', 'text', 'text', 'text', 'text', 'text', 'numeric'],
  headings: [<b>Expected</b>, <b>Cautious</b>, <b>Type</b>, <b>Number</b>, <b>Partner</b>, <b>Due</b>, <b>Amount</b>]
};

//----------------------------------------------------------------------------
// cards
//----------------------------------------------------------------------------
function HistoryBanners({ plan, job, onRetry }) {
  const banners = [];
  if (!plan.history.migrated || !plan.planning.migrated) {
    banners.push(
      <Banner key="migrate" tone="critical" title="Cash planning tables are missing on this database">
        <p>
          Run sql/create_cash_planning_tables.sql once (BankTransactionHistory + CashPlanningItem). Until then the planner only sees bank transactions since go-live and cannot store overrides or
          planned items.
        </p>
      </Banner>
    );
  }
  if (job?.status === 'running') {
    banners.push(
      <Banner key="import" tone="info" title="Importing bank history from Ponto">
        <p>{`${job.imported} older transactions imported so far (${job.scanned} scanned). The recurring flows below get more reliable once this finishes; the page refreshes automatically.`}</p>
      </Banner>
    );
  } else if (job?.status === 'error') {
    banners.push(
      <Banner key="import-error" tone="warning" title="The bank history import failed" action={{ content: 'Retry import', onAction: onRetry }}>
        <p>{job.error}</p>
      </Banner>
    );
  }
  if (plan.history.migrated && plan.history.rows === 0 && job?.status !== 'running' && job?.status !== 'error') {
    banners.push(
      <Banner key="not-imported" tone="info" title="No bank history imported yet" action={{ content: 'Import bank history from Ponto', onAction: onRetry }}>
        <p>
          The recurring flows need about a year of bank transactions. The import normally starts by itself the first time this tab is opened; if it did not, start it here. It runs in the background
          for a few minutes and the page refreshes when it is done.
        </p>
      </Banner>
    );
  }
  if (plan.history.migrated && job?.status !== 'running' && plan.history.completeMonths < 3) {
    banners.push(
      <Banner key="thin" tone="warning" title={`Only ${plan.history.completeMonths} complete month${plan.history.completeMonths === 1 ? '' : 's'} of bank history`}>
        <p>
          The recurring flows (loans, salaries, social secretariat, VAT...) are learned from monthly bank history. With this little data the projections are rough — set a monthly override per category
          below where you know better.
        </p>
      </Banner>
    );
  }
  return banners.length ? <BlockStack gap="300">{banners}</BlockStack> : null;
}

// the weekly table's intercompany column: shown for information, not part of net or balance
const INTERCOMPANY_COLUMN = { key: 'intercompany', label: 'Intercompany', source: 'not in net / balance' };

function WeeklyCard({ plan, horizon, layers, expectedSeries, intercompanySeries }) {
  const [drill, setDrill] = useState(null); // { component, weekStart, weekEnd }
  const hasIntercompany = plan.items.some((item) => item.intercompany);

  const weeks = useMemo(() => {
    const rows = [];
    for (let week = 0; week * 7 + 1 <= horizon; week++) {
      const startOffset = week * 7 + 1;
      const endOffset = Math.min(startOffset + 6, horizon);
      const row = { weekStart: addDaysIso(plan.today, startOffset), weekEnd: addDaysIso(plan.today, endOffset), startOffset, endOffset };
      let net = 0;
      for (const component of COMPONENTS) {
        let sum = 0;
        const flows = plan.componentsDaily[component.key] || [];
        for (let offset = startOffset; offset <= endOffset; offset++) sum += flows[offset] || 0;
        row[component.key] = Math.round(sum);
        if (!component.toggle || layers[component.key]) net += sum;
      }
      row.net = Math.round(net);
      let intercompany = 0;
      for (const component of COMPONENTS) {
        if (component.toggle && !layers[component.key]) continue;
        const flows = plan.intercompanyDaily?.[component.key] || [];
        for (let offset = startOffset; offset <= endOffset; offset++) intercompany += flows[offset] || 0;
      }
      row.intercompany = Math.round(intercompany);
      row.endBalance = expectedSeries[endOffset];
      row.endBalanceCautious = plan.daily[endOffset].cautious;
      row.endBalanceWithIntercompany = intercompanySeries[endOffset];
      rows.push(row);
    }
    return rows;
  }, [plan, horizon, layers, expectedSeries, intercompanySeries]);

  const cell = (week, component) => {
    const value = week[component.key];
    if (!value)
      return (
        <Text tone="subdued" as="span">
          —
        </Text>
      );
    const disabled = component.toggle && !layers[component.key];
    const informative = component.key === INTERCOMPANY_COLUMN.key;
    return (
      <Button variant="plain" onClick={() => setDrill({ component, layers, weekStart: week.weekStart, weekEnd: week.weekEnd, startOffset: week.startOffset, endOffset: week.endOffset })}>
        <Text as="span" tone={disabled ? 'subdued' : informative ? 'magic' : value < 0 ? 'critical' : 'success'} textDecorationLine={disabled ? 'line-through' : undefined}>
          {eur(value)}
        </Text>
      </Button>
    );
  };

  // bank-history columns whose categories are all switched off (nothing projected, no override) are hidden
  const columns = COMPONENTS.filter((component) => !component.categories || (plan.componentsDaily[component.key] || []).some((value) => value));
  const flowColumns = hasIntercompany ? [...columns, INTERCOMPANY_COLUMN] : columns;

  const rows = weeks.map((week) => [
    `${formatShortDate(week.weekStart)} – ${formatShortDate(week.weekEnd).substring(5)}`,
    ...flowColumns.map((component) => cell(week, component)),
    signed(week.net),
    <Text as="span" fontWeight="semibold" tone={week.endBalance < 0 ? 'critical' : undefined}>
      {eur(week.endBalance)}
    </Text>,
    <Text as="span" tone={week.endBalanceCautious < 0 ? 'critical' : 'subdued'}>
      {eur(week.endBalanceCautious)}
    </Text>,
    ...(hasIntercompany
      ? [
          <Text as="span" tone={week.endBalanceWithIntercompany < 0 ? 'critical' : 'subdued'}>
            {eur(week.endBalanceWithIntercompany)}
          </Text>
        ]
      : [])
  ]);

  return (
    <Card>
      <CardHeading title="Week by week" />
      <Text as="p" tone="subdued" variant="bodySm">
        Expected flows per week; click an amount to see what is behind it. Balances at the end of each week for the expected and the cautious scenario, both without intercompany documents
        {hasIntercompany ? '; the intercompany column and the last balance show what those documents would add' : ''}.
      </Text>
      <Box paddingBlockStart="300">
        <DataTable
          verticalAlign="middle"
          increasedTableDensity
          columnContentTypes={['text', ...flowColumns.map(() => 'numeric'), 'numeric', 'numeric', 'numeric', ...(hasIntercompany ? ['numeric'] : [])]}
          headings={[
            <b>Week</b>,
            ...flowColumns.map((c) => (
              <span key={c.key}>
                <b>{c.label}</b>
                <br />
                <span style={{ fontWeight: 400, fontSize: 11, color: '#6D7175' }}>{c.source}</span>
              </span>
            )),
            <b>Net</b>,
            <b>Balance</b>,
            <b>Cautious</b>,
            ...(hasIntercompany ? [<b key="with-intercompany">Incl. interco.</b>] : [])
          ]}
          rows={rows}
        />
      </Box>
      {drill ? <DrilldownModal plan={plan} drill={drill} onClose={() => setDrill(null)} /> : null}
    </Card>
  );
}

function DrilldownModal({ plan, drill, onClose }) {
  const { component, layers, weekStart, weekEnd, startOffset, endOffset } = drill;
  const title = `${component.label}: ${formatShortDate(weekStart)} – ${formatShortDate(weekEnd)}`;
  const inWeek = (item) => item.expectedDate && item.expectedDate >= weekStart && item.expectedDate <= weekEnd;

  let body;
  if (component.key === INTERCOMPANY_COLUMN.key) {
    // the intercompany documents of the selected layers, at their expected date
    const layerOn = (item) => {
      const owner = COMPONENTS.find((c) => c.types?.includes(item.type));
      return !owner?.toggle || layers?.[owner.key];
    };
    const items = plan.items.filter((item) => item.intercompany && inWeek(item) && layerOn(item));
    body = (
      <BlockStack gap="300">
        <Text as="p" variant="bodySm" tone="subdued">
          Open documents with the other group companies. They are left out of the expected and cautious lines; the dotted &quot;incl. intercompany&quot; line shows the balance if they were settled on
          these dates.
        </Text>
        {items.length ? (
          <DataTable {...ITEM_TABLE_PROPS} rows={items.map(itemRow)} />
        ) : (
          <Text as="p" tone="subdued">
            Nothing expected in this week.
          </Text>
        )}
      </BlockStack>
    );
  } else if (component.types && component.key !== 'planned') {
    const items = plan.items.filter((item) => component.types.includes(item.type) && !item.intercompany && inWeek(item));
    if (component.key === 'straight_loan') {
      // simulated loans live in plannedItems but belong in this column
      for (const p of plan.plannedItems) {
        if (p.kind !== 'loan' || p.date < weekStart || p.date > weekEnd) continue;
        items.push({
          type: 'straight_loan',
          id: `simulation_${p.id}_${p.phase}`,
          number: `${p.label} (simulation, ${p.phase})`,
          partnerId: null,
          partnerName: 'Simulation',
          dueDate: p.endDate,
          expectedDate: p.date,
          cautiousDate: p.date,
          amount: p.amount
        });
      }
    }
    body = items.length ? (
      <DataTable {...ITEM_TABLE_PROPS} rows={items.map(itemRow)} />
    ) : (
      <Text as="p" tone="subdued">
        Nothing expected in this week.
      </Text>
    );
  } else if (component.key === 'planned') {
    const items = plan.plannedItems.filter((item) => item.kind !== 'loan' && item.date >= weekStart && item.date <= weekEnd);
    body = (
      <DataTable
        verticalAlign="middle"
        increasedTableDensity
        columnContentTypes={['text', 'text', 'text', 'numeric']}
        headings={[<b>Date</b>, <b>Item</b>, <b>Kind</b>, <b>Amount</b>]}
        rows={items.map((item) => [formatShortDate(item.date), item.label, item.kind === 'monthly' ? 'Monthly' : 'One-off', signed(item.amount)])}
      />
    );
  } else {
    // recurring component: projected per category per day in this week
    const rows = [];
    for (const category of component.categories) {
      const meta = plan.recurring.find((r) => r.key === category);
      const flows = plan.recurringDaily?.[category] || [];
      for (let offset = startOffset; offset <= endOffset; offset++) {
        if (!flows[offset]) continue;
        rows.push([formatShortDate(addDaysIso(plan.today, offset)), meta?.label || category, meta?.override ? 'manual override' : `weighted average, ${meta?.timing || ''}`, signed(flows[offset])]);
      }
    }
    // open supplier invoices of these counterparts sit inside this projection and are not counted again
    const covered = plan.items.filter((item) => item.coveredBy && component.categories.includes(item.coveredBy));
    body = (
      <BlockStack gap="400">
        {rows.length ? (
          <DataTable
            verticalAlign="middle"
            increasedTableDensity
            columnContentTypes={['text', 'text', 'text', 'numeric']}
            headings={[<b>Date</b>, <b>Category</b>, <b>Basis</b>, <b>Amount</b>]}
            rows={rows}
          />
        ) : (
          <Text as="p" tone="subdued">
            Nothing projected in this week.
          </Text>
        )}
        {covered.length ? (
          <BlockStack gap="200">
            <Text as="p" variant="bodySm" tone="subdued">
              {`Open supplier invoices from these counterparts (${eur(
                covered.reduce((sum, item) => sum + item.amount, 0)
              )}) are already inside this projection and are therefore left out of the "Supplier invoices" column:`}
            </Text>
            <DataTable
              verticalAlign="middle"
              increasedTableDensity
              columnContentTypes={['text', 'text', 'text', 'numeric']}
              headings={[<b>Number</b>, <b>Partner</b>, <b>Due</b>, <b>Amount</b>]}
              rows={covered.map((item) => [
                ITEM_TYPES[item.type]?.linkBase ? (
                  <Link removeUnderline url={`${ITEM_TYPES[item.type].linkBase}/${item.id}`}>
                    {item.number || item.id}
                  </Link>
                ) : (
                  String(item.number || item.id)
                ),
                String(item.partnerName || 'Unknown').substring(0, 32),
                item.dueDate ? formatShortDate(item.dueDate) : '—',
                signed(item.amount)
              ])}
            />
          </BlockStack>
        ) : null}
      </BlockStack>
    );
  }

  return (
    <Modal open onClose={onClose} title={title} size="large">
      <Modal.Section>{body}</Modal.Section>
    </Modal>
  );
}

// the bank transactions behind one cell of the recurring table (category × month), or the whole category
function TransactionsModal({ plan, explore, onClose }) {
  const { category, month } = explore;
  const [allMonths, setAllMonths] = useState(false);
  const [query, setQuery] = useState('');
  const [data, setData] = useState(null);
  const label = plan.recurring.find((r) => r.key === category)?.label || category;

  useEffect(() => {
    let cancelled = false;
    setData(null);
    getRequest(`/api/cash-planning/transactions?category=${encodeURIComponent(category)}${allMonths ? '' : `&month=${month}`}`).then((result) => {
      if (!cancelled) setData(result || { error: 'Could not load the transactions' });
    });
    return () => {
      cancelled = true;
    };
  }, [category, month, allMonths]);

  const needle = query.trim().toLowerCase();
  const rows = (data?.transactions || []).filter((tx) => !needle || `${tx.counterpart || ''} ${tx.remittance || ''} ${tx.description || ''}`.toLowerCase().includes(needle));
  const shownTotal = rows.reduce((sum, tx) => sum + tx.amount, 0);

  return (
    <Modal open onClose={onClose} title={`${label} · ${allMonths ? 'last 13 months' : monthLabel(month)}`} size="large">
      <Modal.Section>
        <BlockStack gap="300">
          <InlineStack gap="400" blockAlign="center" wrap>
            <div style={{ minWidth: 280 }}>
              <TextField
                labelHidden
                label="Search transactions"
                placeholder="Search counterpart or message"
                value={query}
                onChange={setQuery}
                autoComplete="off"
                clearButton
                onClearButtonClick={() => setQuery('')}
              />
            </div>
            <Checkbox label="All months of this category" checked={allMonths} onChange={setAllMonths} />
            {data && !data.error ? (
              <Text as="span" tone="subdued" variant="bodySm">
                {needle
                  ? `${rows.length} of ${data.count} transactions shown, ${eur(shownTotal)}`
                  : `${data.count} transactions, ${eur(data.total)}${data.count > data.transactions.length ? ` (first ${data.transactions.length} listed)` : ''}`}
              </Text>
            ) : null}
          </InlineStack>
          {!data ? (
            <SkeletonBodyText lines={6} />
          ) : data.error ? (
            <Banner tone="critical">{data.error}</Banner>
          ) : rows.length ? (
            <DataTable
              verticalAlign="middle"
              increasedTableDensity
              stickyHeader
              columnContentTypes={['text', 'text', 'text', 'numeric']}
              headings={[<b>Date</b>, <b>Counterpart</b>, <b>Message</b>, <b>Amount</b>]}
              rows={rows.map((tx) => [
                <span style={{ whiteSpace: 'nowrap' }}>{formatShortDate(tx.date)}</span>,
                <span style={{ whiteSpace: 'nowrap' }}>{String(tx.counterpart || '—').substring(0, 40)}</span>,
                // the structured message; bank transactions without one (card settlements, bank costs) show the bank text instead
                String(tx.remittance || tx.description || '—')
                  .replace(/\s+/g, ' ')
                  .substring(0, 90),
                signed(tx.amount)
              ])}
            />
          ) : (
            <Text as="p" tone="subdued">
              No transactions.
            </Text>
          )}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}

function RecurringCard({ plan, onSaveOverride, onResetOverride, saving }) {
  const [drafts, setDrafts] = useState({});
  const [selected, setSelected] = useState(null); // category shown alone on the chart, null = all
  const [explore, setExplore] = useState(null); // { category, month } -> transactions modal
  useEffect(() => setDrafts({}), [plan.runAt]);

  // same months as the chart: the complete ones plus the current month so far
  const months = plan.monthlyHistory.map((m) => m.month);
  const partial = new Set(plan.monthlyHistory.filter((m) => m.partial).map((m) => m.month));
  const chartData = plan.monthlyHistory.map((row) => ({
    month: monthLabel(row.month),
    ...Object.fromEntries(Object.keys(RECURRING_COLORS).map((key) => [key, row[key] || 0]))
  }));
  const visibleKeys = selected ? [selected] : Object.keys(RECURRING_COLORS);
  const labelOf = (key) => plan.recurring.find((r) => r.key === key)?.label || key;
  const toggle = (key) => setSelected((current) => (current === key ? null : key));

  const commit = (category) => {
    const draft = drafts[category];
    const meta = plan.recurring.find((r) => r.key === category);
    if (draft == null || draft === '') return;
    const amount = Number(String(draft).replace(',', '.'));
    if (!Number.isFinite(amount) || amount === meta.projectedMonthly) return;
    onSaveOverride(category, amount);
  };

  const rows = plan.recurring.map((r) => [
    <Button variant="plain" onClick={() => toggle(r.key)} ariaPressed={selected === r.key}>
      <InlineStack gap="100" blockAlign="center" wrap={false}>
        <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: RECURRING_COLORS[r.key], opacity: selected && selected !== r.key ? 0.35 : 1 }} />
        <Text as="span" fontWeight={selected === r.key ? 'bold' : 'regular'} tone={selected && selected !== r.key ? 'subdued' : undefined}>
          {r.label}
        </Text>
      </InlineStack>
    </Button>,
    ...months.map((m) =>
      r.monthly[m] ? (
        <Button variant="plain" onClick={() => setExplore({ category: r.key, month: m })} accessibilityLabel={`Transactions of ${r.label} in ${monthLabel(m)}`}>
          {signed(r.monthly[m])}
        </Button>
      ) : (
        signed(r.monthly[m])
      )
    ),
    signed(r.weightedAverage),
    <Text as="span" tone="subdued" variant="bodySm">
      {r.timing}
    </Text>,
    <InlineStack gap="100" blockAlign="center" wrap={false}>
      <div style={{ width: 130 }}>
        <TextField
          labelHidden
          label={`Projected monthly ${r.label}`}
          type="number"
          autoComplete="off"
          value={drafts[r.key] ?? String(r.projectedMonthly)}
          onChange={(value) => setDrafts((d) => ({ ...d, [r.key]: value }))}
          onBlur={() => commit(r.key)}
          disabled={saving || !plan.planning.migrated}
        />
      </div>
      {r.override ? (
        <Button variant="plain" onClick={() => onResetOverride(r.override.id)} disabled={saving}>
          reset
        </Button>
      ) : !r.projectedByDefault ? (
        <Text as="span" tone="subdued" variant="bodySm">
          {r.invoiced ? 'via supplier invoices' : 'irregular, plan it'}
        </Text>
      ) : null}
    </InlineStack>,
    signed(r.next30),
    signed(r.next60)
  ]);

  return (
    <Card>
      <CardHeading title="Recurring flows learned from bank history" />
      <Text as="p" tone="subdued" variant="bodySm">
        Bank transactions that never match an invoice — loan repayments (messages starting with 071-), salaries (&quot;loon&quot;, twice a month), social secretariat, VAT, card settlements, insurance,
        bank costs, staff expenses. Projection = weighted average of the last 5 complete months (recent months weigh more), placed on the days of the month they usually hit. Type a monthly amount to
        override it (negative = money out, changes are saved when you leave the field). The social secretariat is not projected from bank history: its invoices are entered in the system and drive the
        plan through the supplier layer (set an override to project it anyway; its open invoices are then left out so nothing is counted twice, the same rule applies to insurers). VAT &amp; taxes is
        too irregular to extrapolate: it is projected as 0 — add the VAT return and tax prepayments as planned items when you know them.
      </Text>
      <Box paddingBlockStart="300">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="p" tone="subdued" variant="bodySm">
            {selected ? `Showing ${labelOf(selected)} only.` : 'Click a category in the table or in the legend to show only that one.'}
          </Text>
          {selected ? (
            <Button variant="plain" onClick={() => setSelected(null)}>
              Show all categories
            </Button>
          ) : null}
        </InlineStack>
        <div style={{ width: '100%', height: '260px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} stackOffset="sign" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="1 5" />
              <XAxis dataKey="month" />
              <YAxis tickFormatter={formatCompactCurrency} width={80} />
              <ChartTooltip formatter={(value, name) => [eur(value), labelOf(name)]} />
              <Legend formatter={(value) => labelOf(value)} onClick={(entry) => toggle(entry.dataKey || entry.value)} wrapperStyle={{ cursor: 'pointer' }} />
              <ReferenceLine y={0} stroke="#999" />
              {visibleKeys.map((key) => (
                <Bar key={key} dataKey={key} stackId="recurring" fill={RECURRING_COLORS[key]} isAnimationActive={false} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Box>
      <Box paddingBlockStart="300">
        <DataTable
          verticalAlign="middle"
          increasedTableDensity
          fixedFirstColumns={1}
          columnContentTypes={['text', ...months.map(() => 'numeric'), 'numeric', 'text', 'text', 'numeric', 'numeric']}
          headings={[
            <b>Category</b>,
            ...months.map((m) => (
              <b key={m}>
                {monthLabel(m)}
                {partial.has(m) ? ' *' : ''}
              </b>
            )),
            <b>Weighted avg (5 mo)</b>,
            <b>Usually on</b>,
            <b>Projected / month</b>,
            <b>Next 30 d</b>,
            <b>Next 60 d</b>
          ]}
          rows={rows}
        />
        {partial.size ? (
          <Box paddingBlockStart="100">
            <Text as="p" tone="subdued" variant="bodySm">
              * current month so far. The weighted average uses the last 5 complete months. Click a month amount to see the transactions behind it.
            </Text>
          </Box>
        ) : null}
      </Box>
      {explore ? <TransactionsModal plan={plan} explore={explore} onClose={() => setExplore(null)} /> : null}
    </Card>
  );
}

function LoansCard({ plan }) {
  const { loans, straightLoans } = plan;
  const active = loans.contracts.filter((c) => c.active);
  const rows = loans.contracts.map((c) => [
    <span style={{ whiteSpace: 'nowrap' }}>{c.contract}</span>,
    <Badge tone={c.active ? 'success' : undefined}>{c.active ? 'active' : 'no recent payment'}</Badge>,
    String(c.monthsSeen),
    `day ${c.usualDay}`,
    signed(c.monthlyAverage),
    `${formatShortDate(c.lastDate)} · ${eur(c.lastAmount)}`
  ]);
  return (
    <Card>
      <CardHeading title="Bank loans (071-)" />
      <BlockStack gap="200">
        <Text as="p" tone="subdued" variant="bodySm">
          {`${active.length} contracts paid in the last two months, ${eur(active.reduce((sum, c) => sum + c.monthlyAverage, 0))} per month on average. Projected next month: ${eur(
            loans.projectedMonthly
          )} (weighted average of the monthly totals).`}
        </Text>
        {straightLoans.length ? (
          <Banner tone="warning" title="Open straight loans">
            <List>
              {straightLoans.map((loan) => (
                <List.Item key={`${loan.from}_${loan.to}`}>{`${eur(loan.principal)} drawn on ${formatShortDate(loan.drawdownDate)}, repayment scheduled on ${formatShortDate(
                  loan.to
                )} plus an estimated ${eur(loan.interestEstimate)} interest (${(loan.rate * 100).toFixed(1)}% p.a.).`}</List.Item>
              ))}
            </List>
          </Banner>
        ) : null}
        <DataTable
          verticalAlign="middle"
          increasedTableDensity
          columnContentTypes={['text', 'text', 'numeric', 'text', 'numeric', 'text']}
          headings={[<b>Contract</b>, <b>Status</b>, <b>Months seen</b>, <b>Usually on</b>, <b>Avg / month</b>, <b>Last payment</b>]}
          rows={rows}
        />
      </BlockStack>
    </Card>
  );
}

const KIND_LABELS = { one_off: 'Once', monthly: 'Every month', loan: 'Straight loan (simulation)' };

function PlannedItemsCard({ plan, onAdd, onDelete, saving }) {
  const defaultRatePct = plan.straightLoanRate != null ? (plan.straightLoanRate * 100).toFixed(2) : '4';
  const empty = { label: '', amount: '', kind: 'one_off', itemDate: '', endDate: '', dayOfMonth: '1', rate: defaultRatePct, note: '' };
  const [form, setForm] = useState(empty);
  const [error, setError] = useState(null);
  const items = plan.planning.items.filter((item) => item.kind !== 'override');
  const isLoan = form.kind === 'loan';

  const submit = async () => {
    setError(null);
    const amount = Number(String(form.amount).replace(',', '.'));
    if (!form.label.trim()) return setError('Give the item a name.');
    if (!Number.isFinite(amount) || amount === 0) return setError(isLoan ? 'Enter the principal.' : 'Enter an amount (negative = money out).');
    if (form.kind === 'one_off' && !form.itemDate) return setError('Pick the date.');
    if (isLoan && (!form.itemDate || !form.endDate)) return setError('Pick the drawdown and the repayment date.');
    const rate = Number(String(form.rate).replace(',', '.'));
    const result = await onAdd({
      kind: form.kind,
      label: form.label.trim(),
      amount: isLoan ? Math.abs(amount) : amount,
      itemDate: form.itemDate || null,
      endDate: isLoan ? form.endDate : null,
      rate: isLoan && Number.isFinite(rate) ? rate / 100 : null,
      dayOfMonth: form.kind === 'monthly' ? Number(form.dayOfMonth) : null,
      note: form.note || null
    });
    if (result?.error) return setError(result.error);
    setForm({ ...empty, kind: form.kind });
  };

  const whenCell = (item) => {
    if (item.kind === 'monthly') return `day ${item.dayOfMonth}${item.itemDate ? ` from ${formatShortDate(item.itemDate)}` : ''}`;
    if (item.kind === 'loan') return `${formatShortDate(item.itemDate)} → ${formatShortDate(item.endDate)}`;
    return formatShortDate(item.itemDate);
  };
  const amountCell = (item) => {
    if (item.kind !== 'loan') return signed(item.amount);
    const repayment = plan.plannedItems.find((p) => p.id === item.id && p.phase === 'repayment');
    return (
      <InlineStack gap="100" blockAlign="center" wrap={false}>
        <Text as="span" tone="success">{`+${eur(item.amount)}`}</Text>
        <Text as="span" tone="subdued">
          /
        </Text>
        <Text as="span" tone="critical">
          {repayment ? eur(repayment.amount) : `−${eur(item.amount)} + interest`}
        </Text>
        {item.itemDate < plan.today ? <Badge tone="warning">expired</Badge> : null}
      </InlineStack>
    );
  };

  return (
    <Card>
      <CardHeading title="Planned items & loan simulation" />
      <BlockStack gap="300">
        <Text as="p" tone="subdued" variant="bodySm">
          Things the system cannot know yet: an investment, a tax prepayment, a new lease, an expected subsidy (negative amounts leave the account). Or simulate a straight loan to bridge a low period:
          the principal comes in on the drawdown date and goes out again with the interest on the repayment date, so you can see whether the line stays where you want it.
        </Text>
        {error ? <Banner tone="critical">{error}</Banner> : null}
        <FormLayout>
          <FormLayout.Group condensed>
            <TextField label="Item" value={form.label} onChange={(label) => setForm({ ...form, label })} autoComplete="off" disabled={!plan.planning.migrated} />
            <Select label="Kind" options={Object.entries(KIND_LABELS).map(([value, label]) => ({ label, value }))} value={form.kind} onChange={(kind) => setForm({ ...form, kind })} />
            <TextField
              label={isLoan ? 'Principal' : 'Amount'}
              type="number"
              prefix="€"
              value={form.amount}
              onChange={(amount) => setForm({ ...form, amount })}
              autoComplete="off"
              disabled={!plan.planning.migrated}
            />
            {form.kind === 'monthly' ? (
              <TextField label="Day of month" type="number" min={1} max={31} value={form.dayOfMonth} onChange={(dayOfMonth) => setForm({ ...form, dayOfMonth })} autoComplete="off" />
            ) : (
              <TextField label={isLoan ? 'Drawdown date' : 'Date'} type="date" value={form.itemDate} onChange={(itemDate) => setForm({ ...form, itemDate })} autoComplete="off" />
            )}
            {isLoan ? <TextField label="Repayment date" type="date" value={form.endDate} onChange={(endDate) => setForm({ ...form, endDate })} autoComplete="off" /> : null}
            {isLoan ? (
              <TextField
                label="Rate % p.a."
                type="number"
                step={0.01}
                value={form.rate}
                onChange={(rate) => setForm({ ...form, rate })}
                autoComplete="off"
                helpText="observed on your closed straight loans"
              />
            ) : null}
          </FormLayout.Group>
          <InlineStack align="end">
            <Button variant="primary" onClick={submit} loading={saving} disabled={!plan.planning.migrated}>
              {isLoan ? 'Simulate loan' : 'Add item'}
            </Button>
          </InlineStack>
        </FormLayout>
        {items.length ? (
          <DataTable
            verticalAlign="middle"
            increasedTableDensity
            columnContentTypes={['text', 'text', 'text', 'numeric', 'text']}
            headings={[<b>Item</b>, <b>Kind</b>, <b>When</b>, <b>Amount</b>, <b></b>]}
            rows={items.map((item) => [
              item.label,
              KIND_LABELS[item.kind] || item.kind,
              whenCell(item),
              amountCell(item),
              <Button variant="plain" tone="critical" onClick={() => onDelete(item.id)} disabled={saving}>
                remove
              </Button>
            ])}
          />
        ) : (
          <Text as="p" tone="subdued" variant="bodySm">
            No planned items yet.
          </Text>
        )}
      </BlockStack>
    </Card>
  );
}

function UpcomingItemsCard({ plan, horizon, intercompanyGap }) {
  const [typeFilter, setTypeFilter] = useState('all');
  const [showBeyond, setShowBeyond] = useState(false);
  const horizonEnd = addDaysIso(plan.today, horizon);
  const items = plan.items
    .filter((item) => typeFilter === 'all' || item.type === typeFilter || (typeFilter === 'intercompany' && item.intercompany))
    .filter((item) => (showBeyond ? true : item.expectedDate && item.expectedDate <= horizonEnd))
    .slice(0, 60);
  const options = [
    { label: 'All types', value: 'all' },
    ...Object.entries(ITEM_TYPES).map(([value, config]) => ({ label: config.label, value })),
    { label: 'Intercompany only', value: 'intercompany' }
  ];

  // open documents with the other group companies: left out of the expected and cautious lines
  const intercompany = plan.items.filter((item) => item.intercompany);
  const intercompanyIn = intercompany.filter((item) => item.amount > 0).reduce((sum, item) => sum + item.amount, 0);
  const intercompanyOut = intercompany.filter((item) => item.amount < 0).reduce((sum, item) => sum - item.amount, 0);
  return (
    <Card>
      <CardHeading title="Largest open items" />
      <BlockStack gap="300">
        <InlineStack gap="400" blockAlign="center">
          <Select labelHidden label="Type" options={options} value={typeFilter} onChange={setTypeFilter} />
          <Checkbox label="Also show items expected beyond the horizon or not expected at all" checked={showBeyond} onChange={setShowBeyond} />
        </InlineStack>
        <Text as="p" tone="subdued" variant="bodySm">
          {`${plan.doubtful.receivables.count} customer invoices (${eur(plan.doubtful.receivables.amount)}) and ${plan.doubtful.payables.count} supplier invoices (${eur(
            plan.doubtful.payables.amount
          )}) are more than a year overdue and are not expected to move. Supplier credit notes are netted against that supplier's open invoices; a remainder is consumed by future purchases, not paid out.${
            plan.coveredByRecurring?.count
              ? ` ${plan.coveredByRecurring.count} supplier invoices (${eur(
                  plan.coveredByRecurring.amount
                )}) are left out of the supplier layer because their counterpart is projected from bank history as a recurring flow.`
              : ''
          }`}
        </Text>
        {intercompany.length ? (
          <Text as="p" tone="subdued" variant="bodySm">
            {`${intercompany.length} open intercompany documents with ${(plan.intercompanyPartners || []).join(', ')} (to receive ${eur(intercompanyIn)}, to pay ${eur(
              intercompanyOut
            )}, incl. orders) are left out of the expected and cautious lines. Settled with the usual timing they would move ${eur(
              intercompanyGap
            )} within ${horizon} days — the gap to the dotted "incl. intercompany" line.`}
          </Text>
        ) : null}
        <DataTable {...ITEM_TABLE_PROPS} rows={items.map(itemRow)} />
      </BlockStack>
    </Card>
  );
}

function MonthlyHistoryCard({ plan }) {
  const columns = plan.categories.filter((c) => c.key !== 'customer_invoices' && c.key !== 'supplier_invoices');
  const rows = [...plan.monthlyHistory]
    .reverse()
    .map((row) => [`${monthLabel(row.month)}${row.partial ? ' (partial)' : ''}`, ...columns.map((c) => signed(row[c.key])), signed(row.customer_invoices + row.supplier_invoices), signed(row.net)]);
  return (
    <Card>
      <CardHeading title="Monthly bank flows by category" />
      <Text as="p" tone="subdued" variant="bodySm">
        Every bank transaction of the last year, categorized. &quot;Intercompany&quot; holds every transfer to or from the other group companies (never projected). &quot;Other&quot; is the residual
        that did not match a rule — before go-live that is mostly supplier payments and customer receipts; since go-live, matched invoice payments are shown in the invoices column instead.
      </Text>
      <Box paddingBlockStart="300">
        <DataTable
          verticalAlign="middle"
          increasedTableDensity
          columnContentTypes={['text', ...columns.map(() => 'numeric'), 'numeric', 'numeric']}
          headings={[<b>Month</b>, ...columns.map((c) => <b key={c.key}>{c.label.replace(' (matched)', '')}</b>), <b>Invoices (matched)</b>, <b>Net</b>]}
          rows={rows}
        />
      </Box>
    </Card>
  );
}

function HowItWorksCard({ plan, onReimport }) {
  const [open, setOpen] = useState(false);
  const { learning } = plan.stats;
  return (
    <Card>
      <InlineStack align="space-between" blockAlign="center">
        <CardHeading title="How this projection works" />
        <InlineStack gap="300">
          {plan.history.migrated && plan.history.job?.status !== 'running' ? (
            <Button variant="plain" onClick={onReimport}>
              Re-run bank history import
            </Button>
          ) : null}
          <Button variant="plain" onClick={() => setOpen((v) => !v)} ariaExpanded={open} ariaControls="cash-planning-explainer">
            {open ? 'Hide' : 'Show'}
          </Button>
        </InlineStack>
      </InlineStack>
      <Collapsible open={open} id="cash-planning-explainer" transition={{ duration: '200ms', timingFunction: 'ease-in-out' }}>
        <BlockStack gap="200">
          <List>
            <List.Item>
              Starting point is the current balance of the active Ponto bank accounts. The actual line for the past is reconstructed backwards from that balance and the bank transactions.
            </List.Item>
            <List.Item>{`Open customer invoices land on their due date plus the customer's usual delay (median of their last 3 years; ${learning.arPartnerDistributions} customers have their own pattern, the rest use the overall median of ${learning.arDelayMedian} days). Invoices already overdue follow only the historical cases that were at least as late. More than a year overdue = not expected.`}</List.Item>
            <List.Item>{`Open supplier invoices land on the due date plus our usual delay (${learning.apDelayMedian} days overall). Committed Ponto payments land on their execution date. Purchase orders that are not received yet are placed after delivery + ${learning.apFromReceiptMedian} days; when no delivery date is known they are spread over the coming two months.`}</List.Item>
            <List.Item>{`Sales orders that are not invoiced yet convert through the usual order-to-invoice lag (${learning.orderToInvoiceLagMedian} days) plus the customer's payment behavior. This is the least certain layer: switch it off above to see the picture without it.`}</List.Item>
            <List.Item>
              Straight loans (Belfius short-term credit) are recognized from the bank text; an open drawdown is repaid on its end date with an interest estimate from the previous ones.
            </List.Item>
            <List.Item>
              Recurring bank flows (loans, salaries, card, insurance, bank costs, staff expenses) are learned from bank history: weighted average of the last 5 complete months, spread over the days of
              the month they usually hit. Override any of them with a monthly amount when you know better. A counterpart is never counted twice: when its payments are projected from bank history
              (insurers), its open supplier invoices are left out of the supplier layer; when it is not projected (social secretariat, whose invoices are entered in the system), the invoices drive the
              plan. VAT &amp; taxes is not extrapolated (projected as 0): add the return and the prepayments as planned items.
            </List.Item>
            <List.Item>
              Cautious: customers pay at their 75th percentile, invoices more than 90 days overdue and not-yet-invoiced orders are left out, suppliers are paid on the due date. Recurring flows and
              planned items are the same in both scenarios.
            </List.Item>
            <List.Item>{`Intercompany: open invoices, credit notes and orders with ${
              (plan.intercompanyPartners || []).join(', ') || 'the other group companies'
            } (recognized by VAT number) are left out of the expected and cautious lines — they are settled late, netted or moved around as needed. The dotted "incl. intercompany" line adds them back with the timing the other invoices get. Payments already committed in Ponto stay in, and bank transfers with these companies are their own "Intercompany" category, never projected.`}</List.Item>
            <List.Item>
              Not in the model: dividends, one-off investments, new loans, VAT position changes. Add them as planned items, or simulate a straight loan (principal in on the drawdown date, principal
              plus interest out on the repayment date) to see whether it bridges a low period. The numbered markers on the chart show straight-loan repayments, simulated loan legs, planned one-offs
              and single items above the chosen size.
            </List.Item>
          </List>
          <Text as="p" tone="subdued" variant="bodySm">
            {`Computed ${formatShortDate(plan.runAt)} in ${plan.stats.computeMs} ms from ${plan.stats.counts.receivables} customer invoices, ${plan.stats.counts.payables} supplier invoices, ${
              plan.stats.counts.salesOrders
            } sales orders, ${plan.stats.counts.purchaseOrders} purchase orders and ${plan.history.transactionCount} bank transactions since ${formatShortDate(plan.history.earliestTransactionDate)}.`}
          </Text>
        </BlockStack>
      </Collapsible>
    </Card>
  );
}

//----------------------------------------------------------------------------
// the tab
//----------------------------------------------------------------------------
export default function CashPlanningTab() {
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(null);
  // defaults: 60 days, without the two least certain layers (order backlog and open purchase orders)
  const [horizon, setHorizon] = useState(60);
  const [layers, setLayers] = useState({ sales_orders: false, purchase_orders: false });
  const [job, setJob] = useState(null);
  const [saving, setSaving] = useState(false);
  const [eventThreshold, setEventThreshold] = useState(50000); // single items above this get a marker on the chart

  const loadPlan = useCallback(async () => {
    // the server computes the longest window once; the 30/60/90 buttons only slice it client-side
    const result = await getRequest('/api/cash-planning/plan?horizon=90');
    if (!result || result.error) {
      setError(result?.error || 'Could not load the cash plan');
      return;
    }
    setError(null);
    setPlan(result);
    setJob(result.history?.job || null);
  }, []);

  useEffect(() => {
    loadPlan();
  }, [loadPlan]);

  // the one-off Ponto history import runs in the background: poll it, reload when done
  useEffect(() => {
    if (job?.status !== 'running') return undefined;
    const timer = setInterval(async () => {
      const status = await getRequest('/api/cash-planning/history/status');
      if (!status?.job) return;
      setJob(status.job);
      if (status.job.status !== 'running') loadPlan();
    }, 3000);
    return () => clearInterval(timer);
  }, [job?.status, loadPlan]);

  const retryImport = async () => {
    await simplePostRequest('/api/cash-planning/history/backfill', {});
    setJob({ status: 'running', imported: 0, scanned: 0 });
  };

  const saveOverride = async (category, amount) => {
    setSaving(true);
    await simplePostRequest('/api/cash-planning/items', { kind: 'override', category, amount });
    await loadPlan();
    setSaving(false);
  };

  const resetOverride = async (id) => {
    setSaving(true);
    await deleteRequest(`/api/cash-planning/items/${id}`);
    await loadPlan();
    setSaving(false);
  };

  const addItem = async (item) => {
    setSaving(true);
    const response = await simplePostRequest('/api/cash-planning/items', item);
    const body = await response.json().catch(() => ({}));
    if (response.ok) await loadPlan();
    setSaving(false);
    return response.ok ? body : { error: body.error || 'Could not save the item' };
  };

  const deleteItem = async (id) => {
    setSaving(true);
    await deleteRequest(`/api/cash-planning/items/${id}`);
    await loadPlan();
    setSaving(false);
  };

  // shared with the dashboard card: expected balance path (layers re-summed), chart data, markers and the horizon summary
  const expectedSeries = useMemo(() => (plan ? buildExpectedSeries(plan, layers) : []), [plan, layers]);
  const intercompanySeries = useMemo(() => (plan ? buildIntercompanySeries(plan, layers, expectedSeries) : []), [plan, layers, expectedSeries]);
  const chartData = useMemo(() => (plan ? buildBalanceChartData(plan, horizon, expectedSeries, intercompanySeries) : []), [plan, horizon, expectedSeries, intercompanySeries]);
  const events = useMemo(() => (plan ? collectChartEvents(plan, { horizon, layers, eventThreshold }) : []), [plan, horizon, layers, eventThreshold]);
  const summary = useMemo(() => (plan ? summarizeHorizon(plan, { horizon, layers, expectedSeries, intercompanySeries }) : null), [plan, horizon, layers, expectedSeries, intercompanySeries]);

  if (error) {
    return (
      <Banner tone="critical" title="Cash planning could not be loaded" action={{ content: 'Retry', onAction: loadPlan }}>
        <p>{error}</p>
      </Banner>
    );
  }
  if (!plan) {
    return (
      <Card>
        <SkeletonBodyText lines={8} />
      </Card>
    );
  }

  const lastSync = plan.accounts
    .map((a) => a.last_synced_at)
    .filter(Boolean)
    .sort()
    .pop();

  return (
    <BlockStack gap="400">
      <HistoryBanners plan={plan} job={job} onRetry={retryImport} />

      <InlineStack align="space-between" blockAlign="center" wrap>
        <InlineStack gap="400" blockAlign="center" wrap>
          <ButtonGroup variant="segmented">
            <Button pressed={horizon === 30} onClick={() => setHorizon(30)}>
              30 days
            </Button>
            <Button pressed={horizon === 60} onClick={() => setHorizon(60)}>
              60 days
            </Button>
            <Button pressed={horizon === 90} onClick={() => setHorizon(90)}>
              90 days
            </Button>
          </ButtonGroup>
          {COMPONENTS.filter((c) => c.toggle).map((component) => (
            <Checkbox
              key={component.key}
              label={`Include ${component.label.toLowerCase()} (${component.hint})`}
              checked={Boolean(layers[component.key])}
              onChange={(checked) => setLayers({ ...layers, [component.key]: checked })}
            />
          ))}
        </InlineStack>
        <Button onClick={loadPlan}>Refresh</Button>
      </InlineStack>

      <InlineGrid columns={{ xs: 1, sm: 2, md: 5 }} gap="400">
        <StatCard
          title="Balance today"
          value={eur(plan.openingBalance)}
          detail={`${plan.accounts.length} account${plan.accounts.length === 1 ? '' : 's'}${lastSync ? `, synced ${formatShortDate(lastSync)}` : ''}`}
        />
        <StatCard
          title={`Expected in ${horizon} days`}
          value={eur(summary.end)}
          detail={`cautious: ${eur(summary.endCautious)} · incl. intercompany: ${eur(summary.endWithIntercompany)}`}
          tone={summary.end < 0 ? 'critical' : undefined}
        />
        <StatCard
          title="Lowest point (expected)"
          value={eur(summary.lowest.balance)}
          detail={`on ${formatShortDate(summary.lowest.date)} · cautious low ${eur(summary.lowestCautious.cautious)} on ${formatShortDate(summary.lowestCautious.date)} · incl. intercompany ${eur(
            summary.lowestWithIntercompany.balance
          )}`}
          tone={summary.lowest.balance < 0 ? 'critical' : undefined}
        />
        <StatCard title={`Money in, next ${horizon} days`} value={eur(summary.inflow)} detail="expected scenario, selected layers, without intercompany" tone="success" />
        <StatCard title={`Money out, next ${horizon} days`} value={eur(summary.outflow)} detail="invoices, orders, loans, salaries, taxes, planned" tone="critical" />
      </InlineGrid>

      <Card>
        <InlineStack align="space-between" blockAlign="start" wrap>
          <CardHeading title={`Bank balance: last 90 days and next ${horizon} days`} />
          <div style={{ width: 280 }}>
            <Select
              labelHidden
              label="Mark single items above"
              options={[
                { label: 'Mark single items above € 25.000', value: '25000' },
                { label: 'Mark single items above € 50.000', value: '50000' },
                { label: 'Mark single items above € 100.000', value: '100000' },
                { label: 'Only mark loans and planned items', value: '0' }
              ]}
              value={String(eventThreshold)}
              onChange={(value) => setEventThreshold(Number(value))}
            />
          </div>
        </InlineStack>
        <BalanceChart data={chartData} events={events} />
        <EventChips events={events} />
      </Card>

      <WeeklyCard plan={plan} horizon={horizon} layers={layers} expectedSeries={expectedSeries} intercompanySeries={intercompanySeries} />

      <RecurringCard plan={plan} onSaveOverride={saveOverride} onResetOverride={resetOverride} saving={saving} />

      <InlineGrid columns={{ xs: 1, lg: 2 }} gap="400">
        <LoansCard plan={plan} />
        <PlannedItemsCard plan={plan} onAdd={addItem} onDelete={deleteItem} saving={saving} />
      </InlineGrid>

      <UpcomingItemsCard plan={plan} horizon={horizon} intercompanyGap={intercompanySeries[horizon] - expectedSeries[horizon]} />

      <MonthlyHistoryCard plan={plan} />

      <HowItWorksCard plan={plan} onReimport={retryImport} />
    </BlockStack>
  );
}
